import React, { useMemo } from 'react';

import Loading from 'components/layout/loading';
import SelectableCard from 'design-system/SelectableCard';
import SearchBar from 'design-system/SearchBar';
import Checkbox from 'design-system/Checkbox';
import Panel from 'design-system/Panel';
import Typography from 'design-system/Typography';
import { spacing } from 'design-system/tokens';
import useWindowSize from 'hooks/windowSize';
import { useHistory } from 'react-router-dom';
import { pushGtmEvent } from 'analytics/gtm';
import BlockingAlert from 'design-system/BlockingAlert';
import { useNewRefuel } from '../../hooks/useNewRefuel';

import IrregularBaseCard from './IrregularBaseCard';
import {
  BlockedBasesWrap,
  Container,
  SearchHeader,
  SearchRow,
  SearchField,
  RowDivider,
  SectionDivider,
} from './styles';

const BLOCKED_BASES_ALERT_PREFIX = 'Identificamos que uma ou mais bases estão ';
const BLOCKED_BASES_ALERT_STRONG = 'irregulares';
const BLOCKED_BASES_ALERT_SUFFIX =
  '. Para liberação do abastecimento solicite a manutenção.';

const PinIcon = (): React.ReactElement => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const SelectBases = (): React.ReactElement => {
  const history = useHistory();
  const {
    bases,
    selectedBases,
    isLoading,
    addSelectedBase,
    removeSelectedBase,
    addAllSelectedBases,
    removeAllSelectedBases,
    filterTerm,
    handleFilterTerm,
    isBaseBlocked,
    baseBlockReasons,
    hasBlockedBases,
  } = useNewRefuel();

  const goToTechnicalAssistance = (): void => {
    pushGtmEvent({
      event: 'click_cta',
      eventCategory: 'funcao',
      eventAction: 'click',
      eventLabel: 'Solicitar assistência técnica',
      elementType: 'button',
    });
    history.push('/manutencao');
  };

  const orderedBases = useMemo(() => {
    return [...bases].sort((x, y) => {
      const a = x.nickname.toUpperCase();
      const b = y.nickname.toUpperCase();
      if (a === b) return 0;
      return a > b ? 1 : -1;
    });
  }, [bases]);

  const selectableBases = orderedBases.filter(
    (base) => !isBaseBlocked(base.name)
  );

  const allSelected =
    selectableBases.length > 0 &&
    selectableBases.every((base) =>
      selectedBases.some((selected) => selected.name === base.name)
    );

  const handleSelectAll = (): void => {
    if (allSelected) {
      removeAllSelectedBases();
    } else {
      addAllSelectedBases();
    }
  };

  const supplyWayLabel = (way?: string): string => {
    if (!way) return '-';
    return way === 'Agendado' ? 'Automática' : way;
  };

  const toggleBase = (baseName: string, isSelected: boolean): void => {
    if (isSelected) {
      removeSelectedBase(baseName);
    } else {
      addSelectedBase(baseName);
    }
  };

  const [windowWidth] = useWindowSize();
  const searchPlaceholder =
    windowWidth > 0 && windowWidth <= 640
      ? 'Informe o nome ou apelido'
      : 'Informe o nome ou apelido da central de abastecimento';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
      <Panel padding="lg">
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}
        >
          <SearchHeader>
            <Typography as="p" size="xs" weight="medium" color="#262626">
              Busca pelo nome da central de abastecimento
            </Typography>
            <SearchRow>
              <SearchField>
                <SearchBar
                  placeholder={searchPlaceholder}
                  value={filterTerm}
                  onChange={(e) => handleFilterTerm(e.target.value)}
                />
              </SearchField>
              <RowDivider aria-hidden="true" />
              <Checkbox
                label="Selecionar todos"
                checked={allSelected}
                onChange={() => handleSelectAll()}
              />
            </SearchRow>
          </SearchHeader>

          <SectionDivider aria-hidden="true" />

          {hasBlockedBases && (
            <BlockedBasesWrap>
              <BlockingAlert>
                {BLOCKED_BASES_ALERT_PREFIX}
                <strong>{BLOCKED_BASES_ALERT_STRONG}</strong>
                {BLOCKED_BASES_ALERT_SUFFIX}
              </BlockingAlert>
            </BlockedBasesWrap>
          )}

          <Container>
            {isLoading && <Loading />}
            {!isLoading && orderedBases.length === 0 && (
              <Typography as="h2" size="lg" weight="medium" color="#3d3d3d">
                Nenhuma base ativa encontrada.
              </Typography>
            )}
            {!isLoading &&
              orderedBases.map((base) => {
                const isSelected = selectedBases.some(
                  (selected) => selected.name === base.name
                );

                if (isBaseBlocked(base.name)) {
                  return (
                    <IrregularBaseCard
                      key={base.name}
                      title={base.nickname}
                      subtitle={`${base.address}, ${base.number} - ${base.complement}`}
                      reasons={baseBlockReasons(base.name)}
                      onTechnicalAssistance={goToTechnicalAssistance}
                    />
                  );
                }

                return (
                  <SelectableCard
                    key={base.name}
                    icon={<PinIcon />}
                    selectionRole="checkbox"
                    selected={isSelected}
                    onSelect={() => toggleBase(base.name, isSelected)}
                    title={base.nickname}
                    subtitle={`${base.address}, ${base.number} - ${base.complement}`}
                    details={[
                      {
                        label: 'Modelo de abastecimento',
                        value: supplyWayLabel(
                          base?.baseTerritory?.base?.suplayWay
                        ),
                        info: 'Modelo de abastecimento desta central.',
                      },
                      {
                        label: 'Dias de rota',
                        value: base?.baseTerritory?.base?.suplayDays || '-',
                        info: 'Dias em que nossa equipe passa na sua região.',
                      },
                    ]}
                  />
                );
              })}
          </Container>
        </div>
      </Panel>
    </div>
  );
};

export default SelectBases;
